import { t } from 'i18next';
import _ from 'lodash';
import { flux } from '@/core/flux.module';
import { getItemFinder, isHierarchySearchOutput, isStoredTableSearch } from '@/itemFinder/itemFinder.utilities';
import { SeeqNames } from '@/main/app.constants.seeqnames';
import { sqItemsApi } from '@/sdk/api/ItemsApi';
import { sqModelsApi } from '@/sdk/api/ModelsApi';
import { sqTableDefinitionsApi } from '@/sdk/api/TableDefinitionsApi';
import { getTableDefinitionStore, getTableEditorStore } from '@/tableDefinitionEditor/store.utilities';
import { CONTROL_PANE_DISPLAY, EDITOR_TYPES } from '@/tableDefinitionEditor/tableDefinition.constants';
import {
  setShowChildLimitExceededWarning,
  setShowStoredTableSearchWarning,
} from '@/tableDefinitionEditor/tableDefinitionEditor.actions';
import { fetchInputFeaturesFromTableApplication } from '@/tools/models/model.utilities';
import { errorToast } from '@/utilities/toast.utilities';
import type { GridApi } from 'ag-grid-community';
import type { ColumnDefinitionBulkEditInputV1 } from '@/sdk/model/ColumnDefinitionBulkEditInputV1';
import type { ColumnDefinitionInputV1 } from '@/sdk/model/ColumnDefinitionInputV1';
import type { ColumnDefinitionOutputV1 } from '@/sdk/model/ColumnDefinitionOutputV1';
import type { HierarchyOutputV1 } from '@/sdk/model/HierarchyOutputV1';
import type { ItemFinderOutputV1 } from '@/sdk/model/ItemFinderOutputV1';
import type { ItemSearchPreviewV1 } from '@/sdk/model/ItemSearchPreviewV1';
import type { ModelTableApplicationsOutputListV1 } from '@/sdk/model/ModelTableApplicationsOutputListV1';
import type { SearchTypeEnum } from '@/sdk/model/PropertySearchV1';
import type { RowRulesResultV1 } from '@/sdk/model/RowRulesResultV1';
import type { RulesResultV1 } from '@/sdk/model/RulesResultV1';
import type { BatchActionEnum } from '@/sdk/model/TableDefinitionInputV1';
import type { TableDefinitionOutputV1 } from '@/sdk/model/TableDefinitionOutputV1';
import type {
  ColumnDefinition,
  EditorType,
  MaterializedTableItemColumnPropertyPair,
  MaterializedTableOutput,
  ModelToSelect,
  StoredTableSearchFailure,
  StoredTableSearchFailures,
  TableJoinData,
  TreeHierarchy,
} from '@/tableDefinitionEditor/tableDefinition.types';

export function setSubscriptionId(editorType: EditorType, id: string | undefined) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_SET_SUBSCRIBER_ID`, { subscriberId: id });
}

export function setShouldSearchForItems(editorType: EditorType, shouldSearchForItems: boolean) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_SET_SHOULD_SEARCH_FOR_ITEMS`, { shouldSearchForItems });
}

export function setName(editorType: EditorType, name: string) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_SET_NAME`, { name });
}

export function setSearchTypeAI(editorType: EditorType, searchTypeAI: SearchTypeEnum | undefined) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_SET_SEARCH_TYPE_AI`, { searchTypeAI });
}

export function setDescription(editorType: EditorType, description: string) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_SET_DESCRIPTION`, { description });
}

export function setBatchAction(editorType: EditorType, batchAction: BatchActionEnum) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_SET_BATCH_ACTION`, { batchAction });
}

export function setTableDefinition(editorType: EditorType, output: TableDefinitionOutputV1) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_SET_TABLE_DEFINITION`, { tableDefinition: output });
}

export function resetTableDefinition(editorType: EditorType) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_RESET`);
}

export function setMaterializedTable(
  editorType: EditorType,
  materializedTable: MaterializedTableOutput | undefined,
  columnDefinitions?: ColumnDefinition[] | undefined,
) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_SET_MATERIALIZED_TABLE`, { materializedTable, columnDefinitions });
}

export async function setTableDefinitionItemFinder(editorType: EditorType, itemFinder?: ItemFinderOutputV1) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_SET_ITEM_FINDER`, { itemFinder });
  if (itemFinder && itemFinder.searches.some((search) => isStoredTableSearch(search))) {
    const {
      data: { storedTableSearchFailures },
    } = await sqItemsApi.getStoredTableSearchFailures({ id: itemFinder.id });
    if (storedTableSearchFailures.length > 0) {
      // Group failures by storedTableId, then by rowId, and translate messages
      const groupedFailures = storedTableSearchFailures.reduce((acc, failure) => {
        const { storedTableId, rowId, messageKey, searchString } = failure;

        let tableGroup = acc.find((group) => group.storedTableId === storedTableId);
        if (!tableGroup) {
          tableGroup = { storedTableId, failures: [] };
          acc.push(tableGroup);
        }

        let rowFailure = tableGroup.failures.find((f: StoredTableSearchFailure) => f.rowId === rowId);
        if (!rowFailure) {
          rowFailure = { rowId, messages: [] };
          tableGroup.failures.push(rowFailure);
        }

        // Translate the message with searchString interpolation
        const translatedMessage = t(messageKey, { searchString });
        rowFailure.messages.push(translatedMessage);

        return acc;
      }, [] as StoredTableSearchFailures[]);

      setStoredTableFailures(editorType, groupedFailures);

      await getStoredTables(
        editorType,
        groupedFailures.map((groupedFailure) => groupedFailure.storedTableId),
      );

      setShowStoredTableSearchWarning(editorType, true);
    } else {
      setShowStoredTableSearchWarning(editorType, false);
      setStoredTableFailures(editorType, []);
      setStoredTables(editorType, []);
    }
  } else {
    setShowStoredTableSearchWarning(editorType, false);
    setStoredTableFailures(editorType, []);
    setStoredTables(editorType, []);
  }
  if (itemFinder?.lastRunWarnings?.includes('ITEM_FINDER.CHILD_LIMIT_EXCEEDED')) {
    setShowChildLimitExceededWarning(editorType, true);
  } else {
    setShowChildLimitExceededWarning(editorType, false);
  }
}

const getStoredTables = async (editorType: EditorType, storedTableIds: string[]) => {
  try {
    const { data } = await sqItemsApi.searchItems({
      types: [SeeqNames.Types.StoredTable],
      filters: storedTableIds.map((id) => `${SeeqNames.Properties.Id}==${id}`),
      limit: 1000,
    });

    setStoredTables(editorType, data.items ?? []);
  } catch (error) {
    errorToast({ httpResponseOrError: error });
  }
};

export function setTableJoinData(editorType: EditorType, tableJoinData: TableJoinData[] | undefined) {
  const store = getTableDefinitionStore(editorType);
  if (!_.isEqual(store.tableJoinData, tableJoinData)) {
    flux.dispatch(`${editorType}_TABLE_DEFINITION_SET_TABLE_JOIN_DATA`, { tableJoinData });
    setDoTableReload(editorType, true);
  }
}

export function setDoTableReload(editorType: EditorType, doTableReload: boolean) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_SET_DO_TABLE_RELOAD`, { doTableReload });
}

export function setDisplayPropertyForColumn(
  editorType: EditorType,
  uuidColumnToPropertyNamePair: MaterializedTableItemColumnPropertyPair,
) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_SET_PROPERTY_NAME_FOR_UUID_COLUMN`, {
    uuidColumnToPropertyNamePair,
  });
}

export function addPendingTableId(editorType: EditorType, pendingTableId: string) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_ADD_PENDING_TABLE_ID`, { pendingTableId });
}

export function removePendingTableId(editorType: EditorType, pendingTableId: string) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_REMOVE_PENDING_TABLE_ID`, { pendingTableId });
}

export function setEvaluationResults(editorType: EditorType, evaluationResults: { [key: string]: RowRulesResultV1[] }) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_SET_EVALUATION_RESULTS`, { evaluationResults });
}

export function setShowEvaluationDetailModal(editorType: EditorType, show: boolean) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_SET_SHOW_EVALUATION_DETAIL_MODAL`, { show });
}

export function setEvaluationDetailsColumn(
  editorType: EditorType,
  evaluationColumnDef: ColumnDefinition | undefined,
  rulesResult: RulesResultV1[],
) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_SET_EVALUATION_DETAILS_COLUMN`, { evaluationColumnDef, rulesResult });
}

export function setCreatedItemFinderId(editorType: EditorType, id: string | undefined) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_SET_CREATED_ITEM_FINDER_ID`, { createdItemFinderId: id });
}

export function setAllowHiddenColumnsAsInputs(editorType: EditorType, allowHiddenColumnsAsInputs: boolean) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_SET_ALLOW_HIDDEN_COLUMNS_AS_INPUTS`, {
    allowHiddenColumnsAsInputs,
  });
}

export function setAgGridApi(editorType: EditorType, agGridApi: GridApi) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_SET_AG_GRID_API`, { agGridApi });
}

export function setSelectedColumns(editorType: EditorType, selectedColumns: string[]) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_SET_SELECTED_COLUMNS`, { selectedColumns });
}

export function setRowDataIsLoading(editorType: EditorType, rowDataIsLoading: boolean) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_SET_ROW_DATA_IS_LOADING`, { rowDataIsLoading });
}

export function setColumnOptions(editorType: EditorType, columnOptions: TreeHierarchy | undefined) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_SET_COLUMN_OPTIONS`, { columnOptions });
}

export function setScrollToColumnId(editorType: EditorType, scrollToColumnId: string | undefined) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_SET_SCROLL_TO_COLUMN_ID`, { scrollToColumnId });
}

export function setIsApplyingModel(editorType: EditorType, isApplyingModel: boolean) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_SET_IS_APPLYING_MODEL`, { isApplyingModel });
  if (isApplyingModel) {
    flux.dispatch(`${editorType}_SET_CONTROL_PANE`, { controlPane: CONTROL_PANE_DISPLAY.COLUMN_CONTROL });
  }
}

export function setModelInputToSelect(editorType: EditorType, modelInputToSelect: ModelToSelect | undefined) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_SET_MODEL_INPUT_TO_SELECT`, { modelInputToSelect });
}

export function setColumnIdToBeUsedAsModelInput(
  editorType: EditorType,
  columnIdToBeUsedAsModelInput: string | undefined,
) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_SET_COLUMN_ID_TO_BE_USED_AS_MODEL_INPUT`, {
    columnIdToBeUsedAsModelInput,
  });
}

export function setModelColumns(editorType: EditorType, modelColumns: ModelTableApplicationsOutputListV1 | undefined) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_SET_MODEL_COLUMNS`, { modelColumns });
}

export function setInputFeatureColumnIds(editorType: EditorType, inputFeatureColumnIds: string[]) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_SET_INPUT_FEATURE_COLUMN_IDS`, { inputFeatureColumnIds });
}

export function setRequestProgress(
  editorType: EditorType,
  payload: {
    id: string;
    progress: Record<string, unknown>;
    timingInformation?: Record<string, unknown>;
    meterInformation?: Record<string, unknown>;
  },
) {
  flux.dispatch(`REQUEST_SET_PROGRESS`, payload);
}

export function addRowItemIdsInsertedInGroupLevel(editorType: EditorType, ids: string[]) {
  flux.dispatch(`ITEM_ADD_ROWS_TO_FILTER_OUT`, { ids });
}

export function clearRowItemIdsInsertedInGroupLevel(editorType: EditorType) {
  flux.dispatch('ITEM_CLEAR_ROW_IDS');
}

export function setShowCreateModel(editorType: EditorType, show: boolean) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_SET_SHOW_CREATE_MODEL`, { show });
  if (show) {
    flux.dispatch(`${editorType}_SET_CONTROL_PANE`, { controlPane: CONTROL_PANE_DISPLAY.COLUMN_CONTROL });
  }
}

export function setIsEditingModel(editorType: EditorType, isEditingModel: boolean) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_SET_IS_EDITING_MODEL`, { isEditingModel });
}

export function setEditModelPreviewColumns(
  editorType: EditorType,
  editModelPreviewColumns: ColumnDefinitionInputV1[] | ColumnDefinitionBulkEditInputV1[] | undefined,
) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_SET_EDIT_MODEL_PREVIEW_COLUMNS`, { editModelPreviewColumns });
}

export function setColumns(editorType: EditorType, columns: ColumnDefinition[]) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_SET_COLUMNS`, { columns });
}

export function setHierarchiesUsingThisColumnAsGroup(editorType: EditorType, hierarchies: HierarchyOutputV1[]) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_SET_HIERARCHIES_USING_COLUMN`, { hierarchies });
}

export function setColumnToDelete(editorType: EditorType, column: ColumnDefinition | undefined) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_SET_COLUMN_TO_DELETE`, { column });
}

export function setShowRetireModelModal(editorType: EditorType, show: boolean) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_SET_SHOW_RETIRE_MODEL_MODAL`, { show });
}

export function setStoredTableFailures(
  editorType: EditorType,
  storedTableFailures: StoredTableSearchFailures[] | undefined,
) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_SET_STORED_TABLE_FAILURES`, { storedTableFailures });
}

export function setStoredTables(editorType: EditorType, storedTables: ItemSearchPreviewV1[] | undefined) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_SET_STORED_TABLES`, { storedTables });
}

export async function fetchHierarchySearchJoinData(editorType: EditorType, itemFinder?: ItemFinderOutputV1) {
  if (!itemFinder) {
    setTableJoinData(editorType, undefined);
    return undefined;
  }
  const hierarchiesUsed = itemFinder?.searches.filter(isHierarchySearchOutput).map((search) => {
    return {
      hierarchy: search.hierarchy as HierarchyOutputV1,
      tableDefinition: search.hierarchy.tableDefinitionOutput as TableDefinitionOutputV1,
    };
  });

  const tableJoinData: TableJoinData[] | undefined = hierarchiesUsed
    .map(({ hierarchy, tableDefinition }) => {
      const aliasColumnId = hierarchy.aliasColumnId;
      let aliasColumn: ColumnDefinitionOutputV1 | undefined;
      if (aliasColumnId) {
        aliasColumn = tableDefinition.columnDefinitions.find((col) => col.id === aliasColumnId);
      }
      const columnIdsToInclude = aliasColumnId ? [aliasColumnId] : undefined;
      return columnIdsToInclude
        ? {
            tableJoinInput: {
              tableDefinitionId: hierarchy.tableDefinitionOutput.id,
              columnIdsToInclude,
            },
            aliasColumnId,
            aliasColumnName: aliasColumn?.columnName,
          }
        : undefined;
    })
    .filter((join) => join !== undefined);

  setTableJoinData(editorType, tableJoinData);
  return tableJoinData;
}

export async function fetchTableDefinitionItemFinderAndMetadata(
  editorType: EditorType,
  subscriberId: string,
  itemFinder?: ItemFinderOutputV1,
) {
  const foundItemFinder = itemFinder ?? (await getItemFinder(subscriberId));
  await setTableDefinitionItemFinder(editorType, foundItemFinder);
  const tableJoinData = await fetchHierarchySearchJoinData(editorType, foundItemFinder);
  return {
    itemFinder: foundItemFinder,
    tableJoinData,
  };
}

export async function initializeMaterializedTableEditors() {
  await initializeMaterializedTableEditor(EDITOR_TYPES.ITEM);
  await initializeMaterializedTableEditor(EDITOR_TYPES.SCALING);
}

/**
 * Fetch an existing table definition and its associated metadata and set it all in the store
 */
export async function getExistingTableDefinition(tableDefinitionId: string, editorType: EditorType) {
  try {
    const { data: tableDefinition } = await sqTableDefinitionsApi.getTableDefinition({ id: tableDefinitionId });

    await getTableModelApplications(tableDefinitionId, editorType);
    if (tableDefinition.subscription?.id) {
      await fetchTableDefinitionItemFinderAndMetadata(
        editorType,
        tableDefinition.subscription.id,
        tableDefinition.itemFinder,
      );
    }
    // set after fetching and setting metadata, as this setter triggers a table reload
    setTableDefinition(editorType, tableDefinition);
    return tableDefinition;
  } catch (e) {
    if (editorType === 'ITEM') {
      throw e;
    } else {
      errorToast({ httpResponseOrError: e });
    }
  }
}

export async function getTableModelApplications(tableDefinitionId: string, editorType: EditorType) {
  try {
    const { data: modelData } = await sqModelsApi.getTableDefinitionModelApplications({
      id: tableDefinitionId,
    });

    const fetchInputFeatures = async () => {
      const ids = await fetchInputFeaturesFromTableApplication(modelData);
      setInputFeatureColumnIds(editorType, ids);
    };

    if (modelData.applications?.length) {
      setModelColumns(editorType, modelData);
      fetchInputFeatures();
    } else {
      setModelColumns(editorType, undefined);
    }
  } catch (e) {
    errorToast({ httpResponseOrError: e });
  }
}

async function initializeMaterializedTableEditor(editorType: EditorType) {
  const editorStore = getTableEditorStore(editorType);
  const tableStore = getTableDefinitionStore(editorType);
  const tableId = editorStore.tableId;
  if (!!tableId && tableStore.id !== tableId) {
    return getExistingTableDefinition(tableId, editorType);
  }
  return undefined;
}
